import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import { StatusBadge } from '../shared/StatusBadge';
import type { Project, Publication, ProjectMember } from '../../../types/media-sim';

interface WizardStep6Props {
  project: Partial<Project>;
  publications: Publication[];
  members: ProjectMember[];
  onRegister: () => void;
  registering: boolean;
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  const { theme } = useTheme();
  return (
    <Flex gap="sm" style={{ fontSize: '0.9rem' }}>
      <span style={{ fontWeight: 500, color: theme.colors.text.secondary, minWidth: 120 }}>
        {label}:
      </span>
      <span style={{ color: theme.colors.text.primary }}>{value}</span>
    </Flex>
  );
}

export function WizardStep6({
  project,
  publications,
  members,
  onRegister,
  registering,
}: WizardStep6Props) {
  const { theme } = useTheme();

  return (
    <Card variant="elevated">
      <Card.Body>
        <Stack gap="md">
          <h3 style={{ margin: 0, color: theme.colors.text.primary }}>
            Review & Register
          </h3>
          <p style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, margin: 0 }}>
            Review your project configuration before registering. Once registered, the project becomes read-only.
          </p>

          <Card variant="outlined">
            <Card.Body>
              <Stack gap="sm">
                <SummaryRow label="Project Name" value={project.project_name || '—'} />
                <SummaryRow label="Client" value={project.client_name || '—'} />
                <SummaryRow label="Billing Code" value={project.billing_code || '—'} />
                <SummaryRow label="Model" value={project.model_name || '—'} />
                <SummaryRow
                  label="Start Date"
                  value={
                    project.start_date
                      ? new Date(project.start_date).toLocaleDateString()
                      : '—'
                  }
                />
                <SummaryRow
                  label="Topic Terms"
                  value={
                    <Flex gap="xs" wrap="wrap">
                      {(project.topic_terms || []).map((t, i) => (
                        <span
                          key={i}
                          style={{
                            padding: `2px ${spacing.xs}`,
                            backgroundColor: theme.colors.primary[100],
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                          }}
                        >
                          {t}
                        </span>
                      ))}
                    </Flex>
                  }
                />
                <SummaryRow
                  label="Subject Terms"
                  value={
                    <Flex gap="xs" wrap="wrap">
                      {(project.subject_terms || []).map((t, i) => (
                        <span
                          key={i}
                          style={{
                            padding: `2px ${spacing.xs}`,
                            backgroundColor: theme.colors.primary[100],
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                          }}
                        >
                          {t}
                        </span>
                      ))}
                    </Flex>
                  }
                />
                <SummaryRow
                  label="Publications"
                  value={`${publications.length} selected`}
                />
                <SummaryRow
                  label="Members"
                  value={`${members.length} added`}
                />
              </Stack>
            </Card.Body>
          </Card>

          <Button
            variant="solid"
            color="primary"
            onClick={onRegister}
            disabled={registering}
          >
            {registering ? 'Registering...' : 'Register Project'}
          </Button>
        </Stack>
      </Card.Body>
    </Card>
  );
}
